// [PURPOSE]: One-time Terms of Service acceptance prompt for existing users
// [WHY]: GDPR/CCPA + Google Play require explicit consent; users who signed up
//        before ToS was implemented need to accept on next sign-in
// [INTEGRATION]: Rendered in App.jsx when AuthContext.requiresConsent is true
// [CREATED]: Session 5 — Compliance & Safety

import React, { useState } from 'react';
import { ShieldCheck, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import MobileModal from './MobileModal';

/**
 * [PURPOSE]: Blocking modal that requires existing users to accept ToS
 * [UX]: Full-screen overlay — cannot be dismissed without accepting
 * [FLOW]: User checks checkbox → clicks Accept → consent recorded in Firebase
 */
const ConsentPrompt = () => {
  const { acceptTerms, requiresConsent, isAuthenticated, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // [GUARD]: Only show for authenticated users who haven't accepted
  if (!isAuthenticated || !requiresConsent) return null;

  const handleAccept = async () => {
    setError('');
    setLoading(true);
    const result = await acceptTerms();
    if (!result.success) {
      setError(result.error || 'Failed to record consent. Please try again.');
    }
    setLoading(false);
  };

  const handleDecline = async () => {
    // [UX]: Users who decline are signed out — they cannot use the app without accepting
    await logout();
  };

  // [UI-MOBILEMODAL-ADOPTION MOBILEMODAL-DISMISSIBLE-PROP — 2026-07-09]: migrated onto
  // MobileModal now that dismissible={false} exists (no X, no Escape, no backdrop-close
  // — the legal gate closes ONLY through Accept or Decline&SignOut, exactly as before).
  // Identity gradient rides headerClassName (chrome-replacement, wave-2c-2 pattern);
  // the subtitle moves to the top of the body; z-[165] is the literal for
  // Z.CONFIRM_DIALOG(160)+5 (wave-2c-2 Z→class precedent — must stay ABOVE ConfirmDialog).
  return (
    <MobileModal
      isOpen={true}
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck size={20} />
          Updated Terms of Service
        </span>
      }
      ariaLabel="Updated Terms of Service"
      dismissible={false}
      showSaveButton={false}
      overlayClassName="z-[165]"
      maxWidthClass="max-w-md"
      headerClassName="bg-gradient-to-r from-blue-900 to-indigo-900"
    >
      <div className="space-y-4">
          <p className="text-sm text-blue-200">
            Please review and accept our terms to continue using the app.
          </p>
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-600/50 rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <div className="bg-gray-700/50 rounded-lg p-4">
            <p className="text-sm text-gray-300 leading-relaxed">
              We've added Terms of Service and a Privacy Policy to protect both you and us.
              These cover how your data is stored, your rights to export or delete your account,
              and the rules of using the app.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-700/30 transition-colors border border-gray-600">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 accent-blue-500"
            />
            <span className="text-sm text-gray-200 leading-relaxed">
              I agree to the{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </a>
            </span>
          </label>

          <div className="flex gap-3">
            <button
              onClick={handleDecline}
              className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg font-medium transition-colors text-sm"
            >
              Decline & Sign Out
            </button>
            <button
              onClick={handleAccept}
              disabled={!accepted || loading}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Accept & Continue'
              )}
            </button>
          </div>

          {/* Footer — bled to the card edges (negative margin counters the body's
              own p-4/p-6), mirroring the SharedVault wave-2c-2 footer technique. */}
          <div className="-mx-4 -mb-4 px-4 py-3 md:-mx-6 md:-mb-6 md:px-6 bg-gray-900/50 border-t border-gray-700">
            <p className="text-xs text-gray-500 text-center">
              You can delete your account and all data at any time from Settings.
            </p>
          </div>
      </div>
    </MobileModal>
  );
};

export default ConsentPrompt;
